import axios from 'axios';
import { getKeycloak } from './keycloak';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || '/api';

// Create axios instance with auth interceptor
const moodleApiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
moodleApiClient.interceptors.request.use(async (config) => {
  // First try to get token from window (set by keycloak.ts after backend exchange)
  const windowToken = (window as any).__keycloakToken__;
  if (windowToken) {
    config.headers.Authorization = `Bearer ${windowToken}`;
    return config;
  }

  // Fallback to Keycloak instance
  try {
    const keycloak = getKeycloak();
    if (keycloak?.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
  } catch (error) {
    console.warn('Could not get auth token for Moodle API');
  }

  return config;
});

export type MoodleSourceCredentialSource = 'static' | 'vault' | 'environment';

export interface MoodleSource {
  id: string;
  name: string;
  description?: string;

  // Connection type: 'api' uses Moodle plugin REST, 'k8s-exec' uses K8s exec (legacy)
  type?: 'api' | 'k8s-exec';

  // API mode fields (type: 'api')
  moodleUrl?: string;
  moodleToken?: string;
  cptSidecarUrl?: string;
  cptSidecarApiKey?: string;

  // Kubeconfig configuration (choose one)
  kubeconfigId?: string;        // Reference to managed kubeconfig (preferred)
  kubeconfigPath?: string;      // Legacy: direct path (backward compatibility)
  kubeconfigContext?: string;   // Optional context to use from kubeconfig

  namespace?: string;
  podName?: string;
  createdAt: string;
  updatedAt: string;

  // AWS credentials for S3 access (optional - falls back to global config)
  awsCredentialSource?: MoodleSourceCredentialSource;

  // Static credentials (when awsCredentialSource === 'static')
  awsAccessKeyId?: string;
  awsSecretAccessKey?: string;
  awsSessionToken?: string;
  awsRegion?: string;
  awsS3Bucket?: string;

  // Vault credentials (when awsCredentialSource === 'vault')
  awsVaultPathId?: string; // Reference to VaultAWSPath.id from integrations
  // CMI5/cpt-player pod configuration (optional - required for CMI5 content management)
  cptPlayerNamespace?: string;
  cptPlayerPodName?: string;
  // Note: Tenant ID is auto-detected from cpt-player plugin settings in Moodle database
  // RangeOS Environment reference (replaces individual CPT player configuration fields)
  rangeosEnvironmentId?: string; // References a RangeOSEnvironment for all URLs and settings
  // Legacy CMI5 player location configuration (deprecated in favor of rangeosEnvironmentId)
  // These fields are kept for backward compatibility with existing configurations
  cptPlayerLocationName?: string;
  cptPlayerLocationUrl?: string;
  cptPlayerDevOpsApiUrl?: string;
  cptPlayerDevOpsGqlUrl?: string;
  cptPlayerDevOpsGqlSubscriptionsUrl?: string;
  cptPlayerSlideBackground?: string;
  cptPlayerSlideLogo?: string;
}

export interface MoodleCourse {
  id: number;
  shortname: string;
  fullname: string;
  category: number;
  visible: number;
  startdate: number;
  enddate: number;
  timemodified: number;
}

export interface CompetencyFramework {
  id: number;
  shortname: string;
  idnumber: string;
  description: string;
  descriptionformat: number;
}

export interface LearningPathTemplate {
  id: number;
  name: string;
  idnumber: string;
  description: string;
  descriptionformat: number;
  component: string;
}

export interface ExportProgress {
  total: number;
  current: number;
  status: 'preparing' | 'exporting' | 'uploading' | 'complete' | 'error';
  message: string;
}

export interface ExportResult {
  success: boolean;
  exported: string[];
  failed: string[];
  message: string;
}

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  podName?: string;
  namespace?: string;
}

// RangeOS Scenario types
export interface DiscoveredScenario {
  uuid: string;
  name: string;
  cmi5Activities: Array<{
    activityId: number;
    activityName: string;
    auId: string;
    auTitle: string;
  }>;
  hasDraft: boolean;
  draftId?: string;
}

export interface DiscoveredCMI5Activity {
  activityId: number;
  activityName: string;
  tenantId: string;
  cptPlayerCourseId: string;
  aus: Array<{
    auId: string;
    auTitle: string;
    rangeosScenarioUUID?: string;
    rangeosScenarioName?: string;
  }>;
}

export interface CourseScenarioDiscoveryResult {
  courseId: number;
  courseName: string;
  scenarios: DiscoveredScenario[];
  cmi5ActivitiesCount: number;
  cmi5Activities: DiscoveredCMI5Activity[];
}

export const moodleApi = {
  // Source management
  async listSources(): Promise<MoodleSource[]> {
    const response = await moodleApiClient.get('/moodle/sources');
    return response.data;
  },

  async getSource(id: string): Promise<MoodleSource> {
    const response = await moodleApiClient.get(`/moodle/sources/${id}`);
    return response.data;
  },

  async createSource(
    name: string,
    kubeconfigContent: string,
    namespace: string,
    podName: string,
    description?: string,
    awsAccessKeyId?: string,
    awsSecretAccessKey?: string,
    awsRegion?: string,
    awsS3Bucket?: string,
    cptPlayerNamespace?: string,
    cptPlayerPodName?: string,
    rangeosEnvironmentId?: string,
    cptPlayerLocationName?: string,
    cptPlayerLocationUrl?: string,
    cptPlayerDevOpsApiUrl?: string,
    cptPlayerDevOpsGqlUrl?: string,
    cptPlayerDevOpsGqlSubscriptionsUrl?: string,
    cptPlayerSlideBackground?: string,
    cptPlayerSlideLogo?: string
  ): Promise<{ success: boolean; source: MoodleSource }> {
    const response = await moodleApiClient.post('/moodle/sources', {
      name,
      kubeconfigContent,
      namespace,
      podName,
      description,
      awsAccessKeyId,
      awsSecretAccessKey,
      awsRegion,
      awsS3Bucket,
      cptPlayerNamespace,
      cptPlayerPodName,
      rangeosEnvironmentId,
      cptPlayerLocationName,
      cptPlayerLocationUrl,
      cptPlayerDevOpsApiUrl,
      cptPlayerDevOpsGqlUrl,
      cptPlayerDevOpsGqlSubscriptionsUrl,
      cptPlayerSlideBackground,
      cptPlayerSlideLogo,
    });
    return response.data;
  },

  async createSourceFromParams(params: {
    type?: 'api' | 'k8s-exec';
    name: string;
    moodleUrl?: string;
    moodleToken?: string;
    cptSidecarUrl?: string;
    cptSidecarApiKey?: string;
    kubeconfigContent?: string;
    namespace?: string;
    podName?: string;
    description?: string;
    awsAccessKeyId?: string;
    awsSecretAccessKey?: string;
    awsRegion?: string;
    awsS3Bucket?: string;
    cptPlayerNamespace?: string;
    cptPlayerPodName?: string;
    rangeosEnvironmentId?: string;
    cptPlayerLocationName?: string;
    cptPlayerLocationUrl?: string;
    cptPlayerDevOpsApiUrl?: string;
    cptPlayerDevOpsGqlUrl?: string;
    cptPlayerDevOpsGqlSubscriptionsUrl?: string;
    cptPlayerSlideBackground?: string;
    cptPlayerSlideLogo?: string;
  }): Promise<{ success: boolean; source: MoodleSource }> {
    const response = await moodleApiClient.post('/moodle/sources', params);
    return response.data;
  },

  async updateSource(
    id: string,
    updates: Partial<Omit<MoodleSource, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<{ success: boolean; source: MoodleSource }> {
    const response = await moodleApiClient.put(`/moodle/sources/${id}`, updates);
    return response.data;
  },

  async deleteSource(id: string): Promise<{ success: boolean }> {
    const response = await moodleApiClient.delete(`/moodle/sources/${id}`);
    return response.data;
  },

  async testConnection(id: string): Promise<ConnectionTestResult> {
    const response = await moodleApiClient.post(`/moodle/sources/${id}/test`);
    return response.data;
  },

  // Discovery
  async discoverNamespaces(id: string): Promise<string[]> {
    const response = await moodleApiClient.get(`/moodle/sources/${id}/namespaces`);
    return response.data.namespaces;
  },

  async discoverNamespacesFromKubeconfig(
    kubeconfigContent: string,
    awsAccessKeyId?: string,
    awsSecretAccessKey?: string,
    awsRegion?: string
  ): Promise<string[]> {
    const response = await moodleApiClient.post('/moodle/discover-namespaces', {
      kubeconfigContent,
      awsAccessKeyId,
      awsSecretAccessKey,
      awsRegion,
    });
    return response.data.namespaces;
  },

  async discoverPodsFromKubeconfig(
    kubeconfigContent: string,
    namespace: string,
    awsAccessKeyId?: string,
    awsSecretAccessKey?: string,
    awsRegion?: string
  ): Promise<Array<{ name: string; status: string }>> {
    const response = await moodleApiClient.post('/moodle/discover-pods', {
      kubeconfigContent,
      namespace,
      awsAccessKeyId,
      awsSecretAccessKey,
      awsRegion,
    });
    return response.data.pods;
  },

  async discoverPods(id: string, namespace: string): Promise<Array<{ name: string; status: string }>> {
    const response = await moodleApiClient.get(`/moodle/sources/${id}/pods`, {
      params: { namespace },
    });
    return response.data.pods;
  },

  async discoverDeploymentsFromKubeconfig(
    kubeconfigContent: string,
    namespace: string,
    awsAccessKeyId?: string,
    awsSecretAccessKey?: string,
    awsRegion?: string
  ): Promise<Array<{ name: string; replicas: number; availableReplicas: number }>> {
    const response = await moodleApiClient.post('/moodle/discover-deployments', {
      kubeconfigContent,
      namespace,
      awsAccessKeyId,
      awsSecretAccessKey,
      awsRegion,
    });
    return response.data.deployments;
  },

  async discoverDeployments(id: string, namespace: string): Promise<Array<{ name: string; replicas: number; availableReplicas: number }>> {
    const response = await moodleApiClient.get(`/moodle/sources/${id}/deployments`, {
      params: { namespace },
    });
    return response.data.deployments;
  },

  // Content listing
  async listCourses(sourceId: string): Promise<MoodleCourse[]> {
    const response = await moodleApiClient.get(`/moodle/sources/${sourceId}/courses`);
    return response.data.courses;
  },

  async checkCourseConflicts(sourceId: string, courseFilenames: string[]): Promise<any[]> {
    const response = await moodleApiClient.post(`/moodle/sources/${sourceId}/check-conflicts`, {
      courseFilenames,
    });
    return response.data.conflicts;
  },

  async listCompetencyFrameworks(sourceId: string): Promise<CompetencyFramework[]> {
    const response = await moodleApiClient.get(`/moodle/sources/${sourceId}/competency-frameworks`);
    return response.data.frameworks;
  },

  async listLearningPathTemplates(sourceId: string): Promise<LearningPathTemplate[]> {
    const response = await moodleApiClient.get(`/moodle/sources/${sourceId}/learning-path-templates`);
    return response.data.templates;
  },

  // Export operations
  async exportCourses(
    sourceId: string,
    courseIds: number[],
    targetEnvironment: string
  ): Promise<ExportResult> {
    const response = await moodleApiClient.post(`/moodle/sources/${sourceId}/export-courses`, {
      courseIds,
      targetEnvironment,
    });
    return response.data;
  },

  async exportCompetencyFrameworks(
    sourceId: string,
    frameworkIds: number[],
    targetEnvironment: string
  ): Promise<ExportResult> {
    const response = await moodleApiClient.post(
      `/moodle/sources/${sourceId}/export-competency-frameworks`,
      {
        frameworkIds,
        targetEnvironment,
      }
    );
    return response.data;
  },

  async exportLearningPathTemplates(
    sourceId: string,
    templateIds: number[],
    targetEnvironment: string
  ): Promise<ExportResult> {
    const response = await moodleApiClient.post(
      `/moodle/sources/${sourceId}/export-learning-path-templates`,
      {
        templateIds,
        targetEnvironment,
      }
    );
    return response.data;
  },

    // SSE streaming exports with progress
  async exportCoursesWithProgress(
    sourceId: string,
    courseIds: number[],
    targetEnvironment: string,
    onProgress: (progress: { total: number; current: number; status: string; message: string }) => void,
    onJobCreated?: (jobId: string) => void,
    scenarioOptions?: {
      includeScenarios?: boolean;
      selectedScenarioUuids?: Map<number, string[]>;
    }
  ): Promise<ExportResult> {
    return new Promise(async (resolve, reject) => {
      try {
        const keycloak = getKeycloak();
        if (!keycloak || !keycloak.token) {
          throw new Error('Not authenticated');
        }

        // Convert Map to object for JSON serialization
        const scenariosPayload = scenarioOptions?.selectedScenarioUuids
          ? Object.fromEntries(scenarioOptions.selectedScenarioUuids)
          : undefined;

        const response = await fetch(`${API_BASE_URL}/moodle/sources/${sourceId}/export-courses-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${keycloak.token}`,
          },
          body: JSON.stringify({
            courseIds,
            targetEnvironment,
            includeScenarios: scenarioOptions?.includeScenarios,
            selectedScenarioUuids: scenariosPayload,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response body');
        }

        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'job-created') {
                onJobCreated?.(data.jobId);
              } else if (data.type === 'progress') {
                onProgress(data.progress);
              } else if (data.type === 'complete') {
                resolve(data.result);
                return;
              } else if (data.type === 'error' || data.error) {
                reject(new Error(data.error));
                return;
              }
            }
          }
        }
      } catch (error) {
        reject(error);
      }
    });
  },

  async exportCompetencyFrameworksWithProgress(
    sourceId: string,
    frameworkIds: number[],
    targetEnvironment: string,
    onProgress: (progress: { total: number; current: number; status: string; message: string }) => void,
    onJobCreated?: (jobId: string) => void
  ): Promise<ExportResult> {
    return new Promise(async (resolve, reject) => {
      try {
        const keycloak = getKeycloak();
        if (!keycloak || !keycloak.token) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/moodle/sources/${sourceId}/export-competency-frameworks-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${keycloak.token}`,
          },
          body: JSON.stringify({ frameworkIds, targetEnvironment }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response body');
        }

        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'job-created') {
                onJobCreated?.(data.jobId);
              } else if (data.type === 'progress') {
                onProgress(data.progress);
              } else if (data.type === 'complete') {
                resolve(data.result);
                return;
              } else if (data.type === 'error' || data.error) {
                reject(new Error(data.error));
                return;
              }
            }
          }
        }
      } catch (error) {
        reject(error);
      }
    });
  },

  async exportLearningPathTemplatesWithProgress(
    sourceId: string,
    templateIds: number[],
    targetEnvironment: string,
    onProgress: (progress: { total: number; current: number; status: string; message: string }) => void,
    onJobCreated?: (jobId: string) => void
  ): Promise<ExportResult> {
    return new Promise(async (resolve, reject) => {
      try {
        const keycloak = getKeycloak();
        if (!keycloak || !keycloak.token) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/moodle/sources/${sourceId}/export-learning-path-templates-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${keycloak.token}`,
          },
          body: JSON.stringify({ templateIds, targetEnvironment }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response body');
        }

        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'job-created') {
                onJobCreated?.(data.jobId);
              } else if (data.type === 'progress') {
                onProgress(data.progress);
              } else if (data.type === 'complete') {
                resolve(data.result);
                return;
              } else if (data.type === 'error' || data.error) {
                reject(new Error(data.error));
                return;
              }
            }
          }
        }
      } catch (error) {
        reject(error);
      }
    });
  },

  async exportCategoriesWithProgress(
    sourceId: string,
    categoryIds: number[],
    targetEnvironment: string,
    onProgress: (progress: { total: number; current: number; status: string; message: string }) => void,
    onJobCreated?: (jobId: string) => void
  ): Promise<ExportResult> {
    return new Promise(async (resolve, reject) => {
      try {
        const keycloak = getKeycloak();
        if (!keycloak || !keycloak.token) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/moodle/sources/${sourceId}/export-categories-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${keycloak.token}`,
          },
          body: JSON.stringify({ categoryIds, targetEnvironment }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response body');
        }

        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'job-created') {
                onJobCreated?.(data.jobId);
              } else if (data.type === 'progress') {
                onProgress(data.progress);
              } else if (data.type === 'complete') {
                resolve(data.result);
                return;
              } else if (data.type === 'error' || data.error) {
                reject(new Error(data.error));
                return;
              }
            }
          }
        }
      } catch (error) {
        reject(error);
      }
    });
  },

  async listCategories(sourceId: string): Promise<Array<{ id: number; name: string; parent: number; path: string }>> {
    const response = await moodleApiClient.get(`/moodle/sources/${sourceId}/categories`);
    return response.data.categories || response.data;
  },

  // CMI5 Activities
  async listCMI5ActivitiesForCourse(
    sourceId: string,
    courseId: number
  ): Promise<Array<{
    id: number;
    name: string;
    cmi5activityid: string;
    courseinfo: any;
    course: number;
  }>> {
    const response = await moodleApiClient.get(`/moodle/sources/${sourceId}/courses/${courseId}/cmi5-activities`);
    return response.data.activities || response.data;
  },

  // RangeOS Scenarios
  async discoverScenariosForCourse(
    sourceId: string,
    courseId: number,
    forceRefresh: boolean = false
  ): Promise<CourseScenarioDiscoveryResult> {
    const params = forceRefresh ? { refresh: 'true' } : {};
    const response = await moodleApiClient.get(`/moodle/sources/${sourceId}/courses/${courseId}/scenarios/discover`, { params });
    return response.data;
  },

  // Detect CMI5 activities in S3 course backups (before import)
  async detectCMI5InS3Courses(
    courseFilenames: string[],
    s3EnvironmentId: string
  ): Promise<{
    coursesWithCMI5: Array<{
      courseFilename: string;
      hasCMI5: boolean;
      activities: Array<{
        activityName: string;
        activityId: string;
        aus: RC5AU[];
      }>;
      error?: string;
    }>;
  }> {
    const response = await moodleApiClient.post('/courses/detect-cmi5-from-s3', {
      courseFilenames,
      s3EnvironmentId,
    });
    return response.data;
  },

  // Import operations
  async importCourseFromS3(
    sourceId: string,
    s3Key: string,
    categoryId: number,
    s3EnvironmentId?: string
  ): Promise<{ success: boolean; courseId?: number; courseName?: string; message: string }> {
    const response = await moodleApiClient.post(`/moodle/sources/${sourceId}/import-course-from-s3`, {
      s3Key,
      categoryId,
      s3EnvironmentId,
    });
    return response.data;
  },

  // Comprehensive restore: imports course with CMI5 activities and scenarios (with job support)
  async restoreCourseWithCMI5(
    sourceId: string,
    s3Key: string,
    categoryId: number,
    s3EnvironmentId: string,
    rangeosEnvironmentId?: string,
    scenarioMappings?: Record<string, {
      scenarioId: string;
      scenarioName: string;
      mappingType: 'draft' | 'existing';
      draftScenarioUuid?: string;
      draftScenarioVersion?: string;
    }>
  ): Promise<{ jobId: string }> {
    return new Promise(async (resolve, reject) => {
      try {
        const keycloak = getKeycloak();
        if (!keycloak || !keycloak.token) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/moodle/sources/${sourceId}/restore-course-with-cmi5-stream`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${keycloak.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ s3Key, categoryId, s3EnvironmentId, rangeosEnvironmentId, scenarioMappings }),
        });

        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        let jobId: string | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'job_created') {
                jobId = data.jobId;
              } else if (data.type === 'error') {
                reject(new Error(data.message));
                return;
              } else if (data.type === 'complete') {
                if (jobId) {
                  resolve({ jobId });
                } else {
                  reject(new Error('No job ID received'));
                }
                return;
              }
            }
          }
        }

        reject(new Error('Stream ended without completion'));
      } catch (error) {
        reject(error);
      }
    });
  },

  async importCourseFromMoodle(
    sourceMoodleId: string,
    targetMoodleId: string,
    courseId: number,
    categoryId: number
  ): Promise<{ success: boolean; courseId?: number; courseName?: string; message: string }> {
    const response = await moodleApiClient.post(`/moodle/import-course-from-moodle`, {
      sourceMoodleId,
      targetMoodleId,
      courseId,
      categoryId,
    });
    return response.data;
  },

  async importCompetencyFrameworksFromS3(
    sourceId: string,
    s3Keys: string[],
    s3EnvironmentId?: string
  ): Promise<{ success: boolean; message: string; imported: number; failed: number }> {
    const response = await moodleApiClient.post(`/moodle/sources/${sourceId}/import-frameworks-from-s3`, {
      s3Keys,
      s3EnvironmentId,
    });
    return response.data;
  },

  async importCompetencyFrameworksFromS3WithProgress(
    sourceId: string,
    s3Keys: string[],
    s3EnvironmentId?: string
  ): Promise<{ jobId: string }> {
    const keycloak = getKeycloak();
    if (!keycloak || !keycloak.token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`/api/moodle/sources/${sourceId}/import-frameworks-from-s3-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
      body: JSON.stringify({
        s3Keys,
        s3EnvironmentId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to start framework import: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let jobId = '';

    // Read the initial SSE message to get the job ID
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = JSON.parse(line.substring(6));
          if (data.type === 'job_created') {
            jobId = data.jobId;
            reader.cancel(); // Close the stream, we just needed the job ID
            return { jobId };
          }
        }
      }
    }

    throw new Error('Did not receive job ID from server');
  },

  async importLearningPathTemplatesFromS3(
    sourceId: string,
    s3Keys: string[],
    s3EnvironmentId?: string
  ): Promise<{ success: boolean; message: string; imported: number; failed: number }> {
    const response = await moodleApiClient.post(`/moodle/sources/${sourceId}/import-templates-from-s3`, {
      s3Keys,
      s3EnvironmentId,
    });
    return response.data;
  },

  async importLearningPathTemplatesFromS3WithProgress(
    sourceId: string,
    s3Keys: string[],
    s3EnvironmentId?: string
  ): Promise<{ jobId: string }> {
    const keycloak = getKeycloak();
    if (!keycloak || !keycloak.token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`/api/moodle/sources/${sourceId}/import-templates-from-s3-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
      body: JSON.stringify({
        s3Keys,
        s3EnvironmentId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to start template import: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let jobId = '';

    // Read the initial SSE message to get the job ID
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = JSON.parse(line.substring(6));
          if (data.type === 'job_created') {
            jobId = data.jobId;
            reader.cancel(); // Close the stream, we just needed the job ID
            return { jobId };
          }
        }
      }
    }

    throw new Error('Did not receive job ID from server');
  },

  async importCategoriesFromS3(
    sourceId: string,
    s3Key: string,
    s3EnvironmentId?: string
  ): Promise<{ success: boolean; message: string; imported: number; failed: number }> {
    const response = await moodleApiClient.post(`/moodle/sources/${sourceId}/import-categories-from-s3`, {
      s3Key,
      s3EnvironmentId,
    });
    return response.data;
  },

  // Batch import courses from S3 with job support
  async importCoursesFromS3WithProgress(
    sourceId: string,
    s3Keys: string[],
    categoryMappings: Record<string, number> | number,
    s3EnvironmentId?: string,
    conflictResolutions?: Record<string, { resolution: string; newName?: string }>
  ): Promise<{ jobId: string }> {
    return new Promise(async (resolve, reject) => {
      try {
        const keycloak = getKeycloak();
        if (!keycloak || !keycloak.token) {
          throw new Error('Not authenticated');
        }

        // Support both old single categoryId and new per-course mappings
        const body = typeof categoryMappings === 'number'
          ? { s3Keys, categoryId: categoryMappings, s3EnvironmentId, conflictResolutions }
          : { s3Keys, categoryMappings, s3EnvironmentId, conflictResolutions };

        const response = await fetch(`${API_BASE_URL}/moodle/sources/${sourceId}/import-courses-from-s3-stream`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${keycloak.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        let jobId: string | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'job_created') {
                jobId = data.jobId;
              } else if (data.type === 'error') {
                reject(new Error(data.message));
                return;
              } else if (data.type === 'complete') {
                if (jobId) {
                  resolve({ jobId });
                } else {
                  reject(new Error('No job ID received'));
                }
                return;
              }
            }
          }
        }

        reject(new Error('Stream ended without completion'));
      } catch (error) {
        reject(error);
      }
    });
  },

  // Batch import courses from Moodle to Moodle with job support
  async importCoursesFromMoodleWithProgress(
    sourceMoodleId: string,
    targetMoodleId: string,
    courseIds: number[],
    categoryId: number
  ): Promise<{ jobId: string }> {
    return new Promise(async (resolve, reject) => {
      try {
        const keycloak = getKeycloak();
        if (!keycloak || !keycloak.token) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/moodle/import-courses-from-moodle-stream`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${keycloak.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sourceMoodleId, targetMoodleId, courseIds, categoryId }),
        });

        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        let jobId: string | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'job_created') {
                jobId = data.jobId;
              } else if (data.type === 'error') {
                reject(new Error(data.message));
                return;
              } else if (data.type === 'complete') {
                if (jobId) {
                  resolve({ jobId });
                } else {
                  reject(new Error('No job ID received'));
                }
                return;
              }
            }
          }
        }

        reject(new Error('Stream ended without completion'));
      } catch (error) {
        reject(error);
      }
    });
  },

  async exportCMI5ActivityToS3(
    sourceId: string,
    tenantId: string,
    courseId: string,
    bucketId: string,
    envId: string,
    activityName?: string,
    cmi5activityid?: string,
    moodleCourseId?: number
  ): Promise<{ success: boolean; message: string; s3Key: string }> {
    const response = await moodleApiClient.post(
      `/moodle/sources/${sourceId}/cmi5-activity/${tenantId}/${courseId}/export-to-s3`,
      { bucketId, envId, activityName, cmi5activityid, moodleCourseId }
    );
    return response.data;
  },

  async listAllCMI5Activities(sourceId: string): Promise<Array<{
    tenantId: string;
    courseId: string;
    activityId: number;
    activityName: string;
    cmi5activityid: string;
    moodleCourseId: number;
  }>> {
    const response = await moodleApiClient.get(`/moodle/sources/${sourceId}/cmi5-activities`);
    return response.data;
  },

  async exportCMI5ActivitiesWithProgress(
    sourceId: string,
    activities: Array<{ tenantId: string; courseId: string; activityId: number; activityName: string; cmi5activityid?: string; moodleCourseId?: number }>,
    targetEnvironment: string,
    onProgress: (progress: { total: number; current: number; status: string; message: string }) => void,
    onJobCreated?: (jobId: string) => void
  ): Promise<ExportResult> {
    return new Promise(async (resolve, reject) => {
      try {
        const keycloak = getKeycloak();
        if (!keycloak || !keycloak.token) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/moodle/sources/${sourceId}/export-cmi5-activities-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${keycloak.token}`,
          },
          body: JSON.stringify({ activities, targetEnvironment }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response body');
        }

        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'job-created') {
                onJobCreated?.(data.jobId);
              } else if (data.type === 'progress') {
                onProgress(data.progress);
              } else if (data.type === 'complete') {
                resolve(data.result);
                return;
              } else if (data.type === 'error' || data.error) {
                reject(new Error(data.error));
                return;
              }
            }
          }
        }
      } catch (error) {
        reject(error);
      }
    });
  },
};
